import { useState, useEffect, useCallback, useMemo } from 'react'
import WeekAbsenceCells from './WeekAbsenceCells'
import { absenceHours, absenceTotal } from './weekAbsences'
import { useTranslation } from 'react-i18next'
import {
  getTeamMembers, getProjects, getAllocations,
  createAllocation, deleteAllocation, bulkUpsertAllocations,
  getSentWeekAllocations, saveWeek, getWeekAbsences,
} from '../api/client'
import { formatWeekRange } from '../utils/weeks'

export default function EditWeekModal({ week, onClose, onSaved }) {
  const { t } = useTranslation('engineering')
  const [members, setMembers] = useState([])
  const [projects, setProjects] = useState([])
  const [hiddenProjects, setHiddenProjects] = useState([])  // inactive / purchase-only: shown only when they have hours
  const [allocations, setAllocations] = useState([])
  const [loading, setLoading] = useState(true)
  const [restored, setRestored] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editing, setEditing] = useState(null) // { memberId, projectId }
  const [editVal, setEditVal] = useState('')
  const [error, setError] = useState('')
  const [absences, setAbsences] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setRestored(false)
    setError('')
    try {
      const [mems, projs, allocs, abs] = await Promise.all([
        getTeamMembers(),
        getProjects(),
        getAllocations({ week_start: week }),
        // Frozen if the week was saved, derived from the calendar if not.
        getWeekAbsences(week).catch(() => null),
      ])
      setAbsences(abs)
      // Sent weeks clear their live rows after Send — rebuild from the snapshot so the
      // grid isn't empty. Allocations are keyed by week_start, so this never touches
      // the in-progress next-week plan.
      let finalAllocs = allocs
      let didRestore = false
      if (allocs.length === 0) {
        const snapshot = await getSentWeekAllocations(week).catch(() => [])
        if (snapshot.length > 0) {
          try {
            await bulkUpsertAllocations(snapshot.map(a => ({
              team_member_id: a.team_member_id,
              project_id: a.project_id,
              week_start: week,
              hours: a.hours,
            })))
            finalAllocs = await getAllocations({ week_start: week })
            didRestore = true
          } catch {
            // Restore failed — show the week as-is (empty) rather than hang.
          }
        }
      }
      setMembers(mems)
      setProjects(projs.filter(p => p.is_active && !p.is_purchase_only))
      setHiddenProjects(projs.filter(p => !(p.is_active && !p.is_purchase_only)))
      setAllocations(finalAllocs)
      setRestored(didRestore)
    } catch {
      setError(t('editWeek.loadError'))
    } finally {
      setLoading(false)
    }
  }, [week])

  useEffect(() => { load() }, [load])

  // Close on Escape (when not mid-cell-edit).
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape' && !editing) onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose, editing])

  // Hours worked by someone since removed from the team, or on a project since
  // deleted. Those allocation rows survive the deletion so history stays intact,
  // so show them read-only — otherwise they'd count towards the totals with no
  // row or column to explain them.
  const departedMembers = useMemo(() => {
    const live = new Set(members.map(m => m.id))
    const out = new Map()
    for (const a of allocations) {
      if (live.has(a.team_member_id) || out.has(a.team_member_id)) continue
      out.set(a.team_member_id, {
        id: a.team_member_id,
        name: a.team_member_name || `#${a.team_member_id}`,
      })
    }
    return [...out.values()]
  }, [allocations, members])

  // Editable columns for the live projects, then read-only ones for the deleted.
  // An inactive (or purchase-only) project that still has hours this week keeps
  // its column, editable, so its history can be seen and fixed without having
  // to reactivate it; one with no hours stays out of the way.
  const columns = useMemo(() => {
    const withHours = new Set(allocations.filter(a => (a.hours || 0) > 0).map(a => a.project_id))
    const extra = hiddenProjects.filter(p => withHours.has(p.id)).map(p => ({ ...p, inactive: true }))
    const live = [...projects, ...extra]
    const shown = new Set(live.map(p => p.id))
    const gone = new Map()
    for (const a of allocations) {
      if (shown.has(a.project_id) || gone.has(a.project_id) || !a.project_name) continue
      gone.set(a.project_id, {
        id: a.project_id,
        name: a.project_name,
        color: a.project_color,
        readOnly: true,
      })
    }
    return [...live.map(p => ({ ...p, readOnly: false })), ...gone.values()]
  }, [allocations, projects, hiddenProjects])

  const getAllocHours = (memberId, projectId) =>
    allocations.find(a => a.team_member_id === memberId && a.project_id === projectId)?.hours ?? 0
  const getTotalHours = (memberId) =>
    allocations.filter(a => a.team_member_id === memberId).reduce((s, a) => s + a.hours, 0)
  const getProjectTotal = (projectId) =>
    allocations.filter(a => a.project_id === projectId).reduce((s, a) => s + a.hours, 0)
  const grandTotal = allocations.reduce((s, a) => s + a.hours, 0)

  const startEdit = (memberId, projectId) => {
    const cur = getAllocHours(memberId, projectId)
    setEditing({ memberId, projectId })
    setEditVal(cur === 0 ? '' : String(cur))
  }

  const commitEdit = async () => {
    if (!editing) return
    const hours = parseFloat(editVal) || 0
    const existing = allocations.find(
      a => a.team_member_id === editing.memberId && a.project_id === editing.projectId
    )
    if (hours === 0 && existing) {
      await deleteAllocation(existing.id)
    } else if (hours > 0) {
      await createAllocation({
        team_member_id: editing.memberId,
        project_id: editing.projectId,
        week_start: week,
        hours,
      })
    }
    setEditing(null)
    setEditVal('')
    setAllocations(await getAllocations({ week_start: week }))
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') commitEdit()
    if (e.key === 'Escape') { setEditing(null); setEditVal('') }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await saveWeek(week, false) // create-or-refresh the Historic snapshot, keep live rows
      onSaved?.()
      onClose()
    } catch {
      setError(t('editWeek.saveError'))
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-week-title"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 id="edit-week-title" className="text-lg font-bold text-gray-900">{t('editWeek.title')} {formatWeekRange(week)}</h2>
            {restored && <p className="text-xs text-gray-400 mt-0.5">{t('editWeek.restored')}</p>}
          </div>
          <button aria-label={t('editWeek.closeAriaLabel')} onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>

        <div className="px-6 py-4 overflow-auto">
          {loading ? (
            <div className="text-gray-400 text-sm py-8">{t('planWeek.loadingPrev')}</div>
          ) : members.length === 0 || columns.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">{t('editWeek.noTeamOrProjects')}</div>
          ) : (
            <table className="w-full text-sm border-separate border-spacing-0">
              <thead className="sticky top-0 z-10">
                <tr>
                  <th className="sticky left-0 bg-gray-50 border border-gray-200 px-4 py-2.5 text-left font-semibold text-gray-700 min-w-44 z-10">{t('editWeek.engineerHeader')}</th>
                  {columns.map(p => (
                    <th key={p.id} title={p.readOnly ? `${p.name} — ${t('editWeek.deletedProject')}` : p.inactive ? `${p.name} — ${t('editWeek.inactiveProject')}` : p.name} className={`border border-gray-200 px-3 py-2.5 text-center font-semibold min-w-24 bg-gray-50 ${p.readOnly ? 'text-gray-500 italic' : 'text-gray-700'}`}>
                      <div className="flex items-center justify-center gap-1.5">
                        <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: p.color }} />
                        <span className={`truncate max-w-20 text-xs ${p.inactive ? 'italic text-gray-500' : ''}`}>{p.nickname || p.name}</span>
                      </div>
                    </th>
                  ))}
                  {['vacation', 'leave'].map(kind => (
                    <th key={kind}
                        className="bg-gray-50 border border-gray-200 px-3 py-2.5 text-center font-semibold text-amber-800 min-w-24">
                      {t(`editWeek.${kind}Header`)}
                    </th>
                  ))}
                  <th className="bg-gray-50 border border-gray-200 px-3 py-2.5 text-center font-semibold text-gray-700 min-w-20">{t('editWeek.totalHeader')}</th>
                </tr>
              </thead>
              <tbody>
                {members.map(member => {
                  // Absences count toward the total on purpose: the week then
                  // reconciles against the person's own hours, which is what
                  // makes the sheet self-checking.
                  const total = getTotalHours(member.id) + absenceTotal(absences, member.id)
                  const over = total > member.max_weekly_hours
                  const rowBg = over ? 'bg-red-50' : 'bg-white'
                  return (
                    <tr key={member.id} className={rowBg}>
                      <td className={`sticky left-0 ${rowBg} border border-gray-200 px-4 py-2 font-medium text-gray-900 z-10`}>{member.name}</td>
                      {columns.map(project => {
                        const isEditing = editing?.memberId === member.id && editing?.projectId === project.id
                        const hrs = getAllocHours(member.id, project.id)
                        return (
                          <td key={project.id} className="border border-gray-200 px-2 py-1.5 text-center">
                            {project.readOnly ? (
                              <span className="text-gray-500">{hrs > 0 ? `${hrs}h` : '—'}</span>
                            ) : isEditing ? (
                              <input
                                autoFocus
                                type="number"
                                min="0"
                                max="80"
                                step="0.5"
                                className="w-20 text-center border border-gray-400 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                                value={editVal}
                                onChange={e => setEditVal(e.target.value)}
                                onBlur={commitEdit}
                                onKeyDown={handleKeyDown}
                              />
                            ) : (
                              <button
                                onClick={() => startEdit(member.id, project.id)}
                                className={`w-20 text-center rounded-lg px-2 py-1 text-sm transition-colors border ${
                                  hrs > 0
                                    ? 'bg-gray-100 border-gray-200 text-gray-900 font-semibold hover:bg-gray-200'
                                    : 'bg-white border-gray-200 text-gray-300 hover:border-gray-400 hover:text-gray-500'
                                }`}
                              >
                                {hrs > 0 ? `${hrs}h` : t('editWeek.addHoursBtn')}
                              </button>
                            )}
                          </td>
                        )
                      })}
                      <WeekAbsenceCells week={week} data={absences}
                                        memberId={member.id}
                                        onChanged={() => getWeekAbsences(week)
                                          .then(setAbsences).catch(() => {})} />
                      <td className={`border border-gray-200 px-3 py-2 text-center font-semibold ${over ? 'text-red-600' : 'text-gray-700'}`}>
                        {total > 0 ? `${total}h` : '—'}
                      </td>
                    </tr>
                  )
                })}
                {departedMembers.map(member => {
                  const total = getTotalHours(member.id)
                  return (
                    <tr key={`departed-${member.id}`} className="bg-gray-50/60">
                      <td className="sticky left-0 bg-gray-50/60 border border-gray-200 px-4 py-2 text-gray-500 z-10">
                        <span className="italic">{member.name}</span>
                        <span className="block text-xs text-gray-400">{t('editWeek.departed')}</span>
                      </td>
                      {columns.map(project => {
                        const hrs = getAllocHours(member.id, project.id)
                        return (
                          <td key={project.id} className="border border-gray-200 px-2 py-1.5 text-center text-gray-500">
                            {hrs > 0 ? `${hrs}h` : '—'}
                          </td>
                        )
                      })}
                      {['vacation', 'leave'].map(kind => (
                        <td key={kind}
                            className="border border-gray-200 px-2 py-1.5 text-center text-gray-400 bg-gray-50/50">
                          {absenceHours(absences, member.id, kind) > 0
                            ? `${absenceHours(absences, member.id, kind)}h` : '—'}
                        </td>
                      ))}
                      <td className="border border-gray-200 px-3 py-2 text-center font-semibold text-gray-500">
                        {total > 0 ? `${total}h` : '—'}
                      </td>
                    </tr>
                  )
                })}
                <tr className="bg-gray-50 font-semibold">
                  <td className="sticky left-0 bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm text-gray-600 z-10">{t('editWeek.totalHours')}</td>
                  {columns.map(p => {
                    const ph = getProjectTotal(p.id)
                    return (
                      <td key={p.id} className="border border-gray-200 px-2 py-2.5 text-center text-sm text-gray-700">
                        {ph > 0 ? `${ph}h` : '—'}
                      </td>
                    )
                  })}
                  {['vacation', 'leave'].map(kind => {
                    const sum = (absences?.rows || [])
                      .filter(r => r.kind === kind)
                      .reduce((acc, r) => acc + r.hours, 0)
                    return (
                      <td key={kind}
                          className="border border-gray-200 px-2 py-2.5 text-center text-sm text-amber-800 bg-gray-50/50">
                        {sum > 0 ? `${sum}h` : '—'}
                      </td>
                    )
                  })}
                  <td className="border border-gray-200 px-3 py-2.5 text-center text-sm text-gray-900">{grandTotal}h</td>
                </tr>
              </tbody>
            </table>
          )}
        </div>

        {error && <p className="text-xs text-red-500 px-6 pb-2">{error}</p>}
        <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
          <button
            onClick={handleSave}
            disabled={saving || loading || !!editing}
            className="flex-1 bg-gray-900 text-white py-2 rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-50 transition-colors"
          >
            {saving ? t('planWeek.saving') : t('planWeek.save')}
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
          >
            {t('planWeek.sendModal.close')}
          </button>
        </div>
      </div>
    </div>
  )
}
